import { useState, useEffect } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../contexts/ToastContext';
import { 
  CreditCard, 
  CheckCircle, 
  Calendar,
  AlertTriangle,
  Loader
} from 'lucide-react';

const BillingPage = () => {
  const { 
    subscription, 
    isLoading, 
    isOnTrial, 
    isTrialExpired,
    daysLeftInTrial,
    createCheckoutSession,
    createPortalSession,
    refreshSubscription 
  } = useSubscription();
  const { addToast } = useToast();
  
  const [loading, setLoading] = useState(false);

  // Check for success/cancel parameters from Stripe redirects
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success === 'true') {
      addToast('Subscription updated successfully!', 'success');
      // Refresh subscription data to get the latest information
      refreshSubscription().catch(() => {
        // Silent error handling
      });
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      addToast('Subscription update was canceled.', 'info');
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [addToast, refreshSubscription]);

  const plans = {
    starter: {
      name: 'Starter',
      price: '$9',
      duration: '/month',
      features: [
        '1 active forms',
        'Basic analytics',
        '100 responses per month'
      ],
      priceId: 'price_1RwlKZRwStgTZIz3TiwgdA5q',
      cta: 'Choose Plan',
      mostPopular: false,
    },
    professional: {
      name: 'Professional',
      price: '$29',
      duration: '/month',
      features: [
        '15 forms',
        'Unlimited responses',
        'Email notifications'
      ],
      priceId: 'price_1RwlHVRwStgTZIz3AN01Zyj3',
      cta: 'Choose Plan',
      mostPopular: true,
    },
    enterprise: {
      name: 'Enterprise',
      price: '$199',
      duration: '/month',
      features: [
        '100 forms',
        'Unlimited responses',
        'Email notifications'
      ],
      priceId: 'price_1RwlItRwStgTZIz3xjB1fjR4',
      cta: 'Choose Plan',
      mostPopular: false,
    },
  };

  const handleUpgrade = async (priceId: string) => {
    try {
      setLoading(true);
      const url = await createCheckoutSession(priceId);
      
      if (!url) {
        throw new Error('Failed to create checkout session');
      }
      
      window.location.href = url;
    } catch (error) {addToast('Failed to start upgrade process. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      const url = await createPortalSession();
      
      if (!url) {
        throw new Error('No portal URL received');
      }
      
      window.location.href = url;
    } catch (error) {addToast(
        error instanceof Error 
          ? `Failed to open subscription management: ${error.message}`
          : 'Failed to open subscription management. Please try again later.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSubscription = async () => {
    try {
      setLoading(true);
      await refreshSubscription();
      addToast('Subscription data refreshed successfully!', 'success');
    } catch (error) {
      addToast('Failed to refresh subscription data. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader size={40} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your subscription and billing information
          </p>
        </div>
      </div>

      {/* Current Plan Status */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Current Plan</h2>
          
          {isOnTrial && !subscription ? (
            <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Calendar className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Trial Period Active
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>You have {daysLeftInTrial} days remaining in your trial.</p>
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => handleUpgrade(plans.professional.priceId)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Upgrade Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : isTrialExpired && !subscription ? (
            <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Trial Period Expired
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>Your trial has expired. Please select a plan to continue using the service.</p>
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => handleUpgrade(plans.starter.priceId)}
                      disabled={loading}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Select a Plan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : subscription ? (
            <div className="mt-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {subscription.plan === 'starter' ? 'Starter' :
                   subscription.plan === 'professional' ? 'Professional' :
                   subscription.plan === 'enterprise' ? 'Enterprise' :
                   subscription.plan === 'unknown' ? 'Loading Plan...' :
                   subscription.plan || 'Unknown'} Plan
                </span>
                {subscription.status === 'active' && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                )}
              </div>
              
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Billing Period</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    Monthly
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Next Payment</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {subscription.current_period_end ? 
                      new Date(subscription.current_period_end).toLocaleDateString() : 
                      'Not available'
                    }
                  </dd>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={handleManageSubscription}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <CreditCard className="mr-2 h-5 w-5 text-gray-400" />
                  Manage Subscription
                </button>
                <button
                  type="button"
                  onClick={handleRefreshSubscription}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {loading ? (
                    <Loader className="mr-2 h-5 w-5 text-gray-400 animate-spin" />
                  ) : (
                    <Calendar className="mr-2 h-5 w-5 text-gray-400" />
                  )}
                  Refresh Data
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-sm text-gray-500">
                You are currently on the Free plan
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Available Plans */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {subscription ? 'Change Plan' : 'Available Plans'}
          </h2>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {Object.entries(plans).map(([key, plan]) => {
              const isCurrentPlan = subscription?.plan === key;
              
              return (
                <div
                  key={key}
                  className={`relative rounded-lg border ${
                    isCurrentPlan 
                      ? 'border-green-500 bg-green-50' 
                      : plan.mostPopular 
                        ? 'border-blue-500 shadow-lg' 
                        : 'border-gray-200'
                  } p-6`}
                >
                  {isCurrentPlan && (
                    <span className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 transform rounded-full bg-green-500 px-3 py-0.5 text-xs font-medium text-white">
                      Current Plan
                    </span>
                  )}
                  {!isCurrentPlan && plan.mostPopular && (
                    <span className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 transform rounded-full bg-blue-500 px-3 py-0.5 text-xs font-medium text-white">
                      Most Popular
                    </span>
                  )}
                  
                  <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                  <p className="mt-4">
                    <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                    <span className="text-base font-medium text-gray-500">{plan.duration}</span>
                  </p>
                  
                  <ul className="mt-6 space-y-4">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <p className="ml-3 text-base text-gray-500">{feature}</p>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-8">
                    <button
                      type="button"
                      onClick={() => handleUpgrade(plan.priceId)}
                      disabled={loading || isCurrentPlan}
                      className={`w-full rounded-md px-4 py-2 text-sm font-medium ${
                        isCurrentPlan
                          ? 'bg-gray-100 text-gray-500 border border-gray-300 cursor-not-allowed'
                          : plan.mostPopular
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-white text-blue-600 border border-blue-600 hover:bg-blue-50'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {loading ? (
                        <Loader className="animate-spin h-5 w-5 mx-auto" />
                      ) : isCurrentPlan ? (
                        'Selected Plan'
                      ) : (
                        plan.cta
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;